import React from 'react';
import { Flex, Text, Tooltip, UnstyledButton } from '@mantine/core';
import {
    IconAlertCircle,
    IconEyeOff,
    IconPaperclip,
    IconX,
} from '@tabler/icons-react';
import { PriceSurveyCompetitor } from '../../codegen/graphql';
import type { CompetitorDrawerData } from './types';
import { PropagationStopper } from '../../components/common';

type CompetitorColumnHeaderProps = {
    comp: PriceSurveyCompetitor;
    onCompetitorClick?: (data: CompetitorDrawerData) => void;
    onRemoveFromSurvey?: (priceSurveyCompetitorId: string) => void;
    showAttachmentButton: boolean;
    showRemoveButton?: boolean;
    showCollapseSectionButton?: boolean;
    onToggleCompetitorsSection?: () => void;
};

export function CompetitorColumnHeader({
    comp,
    onCompetitorClick,
    onRemoveFromSurvey,
    showAttachmentButton,
    showRemoveButton = false,
    showCollapseSectionButton = false,
    onToggleCompetitorsSection,
}: CompetitorColumnHeaderProps) {
    const handleAttachmentClick = () => {
        if (!onCompetitorClick) return;
        const telNo = comp.competitor?.telNo;
        onCompetitorClick({
            priceSurveyCompetitorId: comp.id,
            competitor: {
                id: comp.competitor?.id ?? comp.competitorId,
                name: comp.competitor?.name ?? 'Competitor',
                email: comp.competitor?.email ?? undefined,
                address: comp.competitor?.address ?? undefined,
                isMajor: comp.competitor?.isMajor,
                telNo: telNo
                    ? {
                          countryCode: telNo.countryCode ?? undefined,
                          dialingCode: telNo.dialingCode ?? undefined,
                          number: telNo.number ?? undefined,
                      }
                    : undefined,
            },
        });
    };

    const handleRemoveClick = () => {
        onRemoveFromSurvey?.(comp.id);
    };

    const competitorName = comp.competitor?.name || 'Competitor';
    const attachmentCount = comp.proofOfSurveyCompetitorPricing?.length ?? 0;
    const hasAttachments = attachmentCount > 0;

    return (
        <Flex align="center" justify="space-between" gap={4} w="100%">
            <Flex align="center" gap={4} className="min-w-0 flex-1">
                <Tooltip label={competitorName} withArrow>
                    <Text size="sm" fw={500} c="white" className="truncate">
                        {competitorName}
                    </Text>
                </Tooltip>
            </Flex>
            <PropagationStopper>
                <Flex align="center" gap={4} className="shrink-0">
                    {showCollapseSectionButton &&
                        onToggleCompetitorsSection && (
                            <Tooltip
                                label="Hide competitor columns"
                                withArrow
                                position="bottom"
                                openDelay={300}
                            >
                                <UnstyledButton
                                    type="button"
                                    onClick={onToggleCompetitorsSection}
                                    aria-label="Hide competitor columns"
                                    className="flex h-[26px] w-[26px] items-center justify-center rounded-md hover:bg-white/15"
                                >
                                    <IconEyeOff
                                        size={16}
                                        stroke={2}
                                        className="text-white"
                                    />
                                </UnstyledButton>
                            </Tooltip>
                        )}
                    {showAttachmentButton && onCompetitorClick && (
                        <Tooltip
                            label={
                                hasAttachments
                                    ? `${attachmentCount} attachment${
                                          attachmentCount > 1 ? 's' : ''
                                      }`
                                    : 'No attachments — click to add'
                            }
                            withArrow
                        >
                            <UnstyledButton
                                onClick={handleAttachmentClick}
                                aria-label="Upload attachment"
                                className={`flex h-[26px] items-center gap-1 rounded-md px-2 ${
                                    hasAttachments
                                        ? 'bg-blue-600 hover:bg-blue-700'
                                        : 'bg-orange-500 hover:bg-orange-600'
                                }`}
                            >
                                <IconPaperclip
                                    size={15}
                                    className="text-white"
                                />
                                {hasAttachments ? (
                                    <span className="inline-flex h-[15px] min-w-[15px] items-center justify-center rounded-full border-[1.5px] border-solid border-white bg-transparent text-[9px] font-bold leading-none text-white">
                                        {attachmentCount}
                                    </span>
                                ) : (
                                    <IconAlertCircle
                                        size={15}
                                        className="text-white"
                                    />
                                )}
                            </UnstyledButton>
                        </Tooltip>
                    )}
                    {showRemoveButton && onRemoveFromSurvey && (
                        <UnstyledButton
                            onClick={handleRemoveClick}
                            aria-label="Remove competitor from survey"
                            className="flex h-[26px] w-[26px] items-center justify-center rounded-md bg-red-600 hover:bg-red-700"
                        >
                            <IconX
                                size={15}
                                stroke={3}
                                className="text-white"
                            />
                        </UnstyledButton>
                    )}
                </Flex>
            </PropagationStopper>
        </Flex>
    );
}
